import React from 'react';
import { Page, Text, View, Document, Link } from '@react-pdf/renderer';
import { ATSResumeJson } from '@/types/resume';
import { styles } from '@/components/common/AtsResumePdfStyles';
import { parseRichText, getValidUrl } from './PdfUtils';
import { PdfHeader } from './PdfHeader';
import { PdfExperience } from './PdfExperience';
import { PdfEducation } from './PdfEducation';

interface Props {
  data: ATSResumeJson;
  showChanges?: boolean;
}

export const AtsResumePDF = React.memo<Props>(({ data, showChanges = false }) => {
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        
        <PdfHeader data={data} showChanges={showChanges} />

        {/* SUMMARY */}
        {data.summary && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Summary</Text>
            <View style={styles.sectionUnderline} />
            <Text style={styles.summaryText}>{parseRichText(data.summary, showChanges)}</Text>
          </View>
        )}

        {/* SKILLS */}
        {data.skills && data.skills.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Skills</Text>
            <View style={styles.sectionUnderline} />
            {data.skills.map((skillGroup, idx) => (
              <View key={idx} style={styles.skillsRow}>
                <Text style={styles.skillCategory}>{parseRichText(skillGroup.category + ':', showChanges)}</Text>
                <Text style={styles.skillItems}>{parseRichText(skillGroup.items.join(', ').replace(/\*\*/g, ''), showChanges)}</Text>
              </View>
            ))}
          </View>
        )}

        <PdfExperience data={data} showChanges={showChanges} />

        {/* PROJECTS */}
        {data.projects && data.projects.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Projects</Text>
            <View style={styles.sectionUnderline} />
            {data.projects.map((proj, idx) => (
              <View key={idx} style={styles.projectItem}>
                <View wrap={false}>
                  <View style={styles.projectHeader}>
                    <Text style={styles.projectName}>
                      {parseRichText(proj.name, showChanges)}
                      {proj.description && <Text style={styles.projectDesc}> — {parseRichText(proj.description, showChanges)}</Text>}
                    </Text>
                  </View>
                  {proj.url && getValidUrl(proj.url) && (
                    <Link src={getValidUrl(proj.url)!} style={[styles.jobMeta, styles.link, { marginBottom: 4 }]}>
                      {proj.url.replace(/^https?:\/\/(www\.)?/, '')}
                    </Link>
                  )}
                  {proj.url && !getValidUrl(proj.url) && (
                    <Text style={[styles.jobMeta, proj.url === 'ACTION_REQUIRED_URL' ? { color: '#dc2626' } : {}, { marginBottom: 4 }]}>
                      {proj.url === 'ACTION_REQUIRED_URL' ? 'Action Required' : proj.url}
                    </Text>
                  )}
                  {proj.highlights && proj.highlights.flatMap(h => h.split('\n')).map(h => h.replace(/^[-\s•]+/, '').trim()).filter(h => h.replace(/^[\W\s]+$/, '').length > 0).slice(0, 1).map((highlight, hIdx) => (
                    <View key={`first-${hIdx}`} style={styles.bulletRow} wrap={false}>
                      <Text style={styles.bulletPoint}>•</Text>
                      <Text style={styles.bulletText}>{parseRichText(highlight, showChanges)}</Text>
                    </View>
                  ))}
                </View>
                {proj.highlights && proj.highlights.flatMap(h => h.split('\n')).map(h => h.replace(/^[-\s•]+/, '').trim()).filter(h => h.replace(/^[\W\s]+$/, '').length > 0).slice(1).map((highlight, hIdx) => (
                  <View key={`rest-${hIdx}`} style={styles.bulletRow} wrap={false}>
                    <Text style={styles.bulletPoint}>•</Text>
                    <Text style={styles.bulletText}>{parseRichText(highlight, showChanges)}</Text>
                  </View>
                ))}
              </View>
            ))}
          </View>
        )}

        <PdfEducation data={data} showChanges={showChanges} />

        {/* HONORS */}
        {data.honors && data.honors.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Honors & Awards</Text>
            <View style={styles.sectionUnderline} />
            {data.honors.map((honor, idx) => (
              <View key={idx} style={{ marginBottom: 3 }}>
                <Text>{parseRichText(honor, showChanges)}</Text>
              </View>
            ))}
          </View>
        )}

        {/* LANGUAGES */}
        {data.languages && data.languages.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Languages</Text>
            <View style={styles.sectionUnderline} />
            <Text>{data.languages.join(' | ')}</Text>
          </View>
        )}
      </Page>
    </Document>
  );
});
